import { icons } from "@core/assets";
import {
  Header,
  SideBar,
  type MenuOption,
  type SideBarItem,
} from "@core/components";
import { useSidebarContext } from "@core/context";
import { cn } from "@core/utils";
import ProfileSettings from "@core/components/ProfileMenu";
import {
  StorageIndicator,
  SubscriptionBannerController,
} from "@modules/subscription";
import { useGetUserWorkspaces } from "@modules/auth/select-workspace/hooks/useGetUserWorkspaces";
import type { TUserWorkspace } from "@modules/auth/select-workspace/types/TUserWorkspace";
import { useWorkspaceContext } from "@modules/workspace/context";
import { WorkspaceGuard } from "@modules/workspace/guards";
import { useTranslate } from "../hooks/useTranslate";
import { Outlet, useNavigate } from "react-router-dom";

export const WorkspaceConsole = () => {
  const { workspace, workspaceId } = useWorkspaceContext();
  const { data: workspacesData } = useGetUserWorkspaces();
  const { collapsed } = useSidebarContext();
  const navigate = useNavigate();
  const { t } = useTranslate();

  const sideBarItems: SideBarItem[] = [
    {
      title: t("sidebar.general"),
      link: [
        {
          name: t("sidebar.members"),
          link: `/workspace/${workspaceId}/console/members`,
          icon: icons["users"],
          type: "link",
        },
        {
          name: t("sidebar.licenses"),
          link: `/workspace/${workspaceId}/console/licenses`,
          icon: icons["badge-check"],
          type: "link",
        },
        {
          name: t("sidebar.projects"),
          link: `/workspace/${workspaceId}/console/projects`,
          icon: icons["panels-top-left"],
          type: "link",
        },
        {
          name: t("sidebar.activity"),
          link: `/workspace/${workspaceId}/console/activity`,
          icon: icons["activity"],
          type: "link",
          badge: "beta",
        },
      ],
    },
    {
      title: t("sidebar.other"),
      footer: true,
      link: [
        {
          name: t("sidebar.settings"),
          link: `/workspace/${workspaceId}/console/settings`,
          icon: icons["settings"],
          type: "link",
        },
      ],
    },
  ];

  const items: TUserWorkspace[] = (
    workspacesData?.items ?? ([] as TUserWorkspace[])
  ).filter((ws: TUserWorkspace) => ws.status === "active");

  const workspaceOptions: MenuOption[] = items.map((ws) => ({
    id: ws.id,
    name: ws.name,
    label: ws.name,
  }));

  const switchWorkspace = (value: string) => {
    const selected = items.find((ws) => ws.name === value || ws.id === value);
    if (!selected) return;

    const target =
      selected.role === "admin"
        ? `/workspace/${selected.id}/console/members`
        : `/workspace/${selected.id}/project-portal`;

    navigate(target, { replace: true });
  };

  return (
    <div className="hidden md:block min-h-screen w-full">
      <SideBar
        backHome
        link="/select-workspace"
        items={sideBarItems}
        workspaceHeaderTitle={workspace.name}
        workspaceHeadeBadgeTitle="Early Access"
        className="md:w-[13rem] lg:w-[17.125rem]"
        menuOptions={workspaceOptions}
        onWorkspaceSelect={switchWorkspace}
        is_switch
      />
      <div
        className={cn(
          "min-w-0 transition-[margin] duration-200 ease-in-out",
          collapsed ? "ml-[4.3125rem]" : "md:ml-[13rem] lg:ml-[17.125rem]",
        )}
      >
        <Header>
          <StorageIndicator />
          <ProfileSettings />
        </Header>
        <main
          className={`mx-auto w-full flex flex-col gap-2 max-w-[2000px] px-5 2xl:px-10 py-3`}
        >
          <SubscriptionBannerController />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export const WorkspaceLayout = () => <WorkspaceGuard />;
